import JSON5 from 'json5'
import { Alert } from './AlertTypes'

export interface DeviceEvent {
  eventId: number
  deviceId: number
  eventType: string
  eventTime: string
  scanId: string
  fileId: string
  eventDetails?: string
  alert?: Alert
}

export interface DeviceEventResponse {
  events: DeviceEvent[]
  totalPages: number
}

export enum DeviceEventType {
  ALERT_RAISED = 'alert_raised',
  ALERT_UPDATED = 'alert_updated',
  ALERT_CLOSED = 'alert_closed',
  APP_VERSION_CHANGED = 'app_version_changed',
  BIOMETRICS_CHANGED = 'biometrics_changed',
  BOOT_TIME = 'boot_time_changed',
  CONTAINERMANAGERD_BOOT = 'containermanagerd_boot',
  COUNTRY_CODE = 'country_code_changed',
  CRASHLOG_DELETED = 'crashlog_deleted',
  CRASHLOG_CREATED = 'crashlog_created',
  EMPTY_DIRECTORY = 'empty_directory',
  IS_ADB_ENABLED = 'is_adb_enabled_changed',
  IS_BIOMETRICS_TRUSTED = 'is_biometrics_trusted_changed',
  IS_DEVELOPER_MODE_ENABLED = 'is_developer_mode_enabled_changed',
  IS_NOTIFICATIONS_ENABLED_CHANGED = 'is_notifications_enabled_changed',
  IS_PLAY_PROTECT_ENABLED = 'is_play_protect_enabled_changed',
  IS_SIDELOADING_ENABLED = 'is_sideloading_enabled_changed',
  IS_WORK_PROFILE_CHANGED = 'is_work_profile_changed',
  LOCKDOWN_ENABLED = 'lockdown_enabled_changed',
  OS_VERSION_CHANGED = 'os_version_changed',
  OS_BUILD_VERSION_CHANGED = 'os_build_version_changed',
  PROCESS_CRASH = 'process_crash',
  RECORD_DELETED = 'record_deleted',
  ROOT_DETECTED = 'root_detected_changed',
  SCAN_RECEIVED = 'scan_received',
  SHUTDOWN_REBOOT = 'shutdown_reboot',
  SCREEN_LOCK_CHANGED = 'screen_lock_changed',
  SECURITY_PATCH = 'security_patch_changed',
  SMISHING_ENABLED = 'smishing_enabled_changed',
  SYSDIAGNOSE_CAPTURED = 'sysdiagnose_captured',
  SYSDIAGNOSE_CREATED = 'sysdiagnose_created',
  SYSDIAGNOSE_DELETED = 'sysdiagnose_deleted',
  FS_METADATA_CREATED = 'fs_metadata_created',
  TELCO = 'telco_changed',
  TELCO_SET = 'telco_set',
  COUNTRY_CODE_SET = 'country_code_set',
  DEVICE_OUT_OF_POLICY = 'device_out_of_policy',
  DEVICE_IN_POLICY = 'device_in_policy',
  IVERIFY_RECENTLY_INSTALLED = 'iverify_recently_installed',
}

export enum DeviceEventCategory {
  ALERT = 'alert',
  CRASH = 'crash',
  SCANS = 'scan',
  SYSTEM = 'system',
  PROTECTION_MEASURES = 'protection_measures',
  POLICY = 'policy',
  NETWORK = 'network',
  APP = 'app',
  OS_SECURITY = 'os_security',
  DIAGNOSTICS = 'diagnostics',
}

export const getEventName = (eventType: string) => {
  switch (eventType) {
    case DeviceEventType.SHUTDOWN_REBOOT:
      return 'Shutdown/Reboot'
    case DeviceEventType.SCAN_RECEIVED:
      return 'Scan Received'
    case DeviceEventType.CONTAINERMANAGERD_BOOT:
      return 'Container Managed Boot'
    case DeviceEventType.IS_ADB_ENABLED:
      return 'ADB Changed'
    case DeviceEventType.IS_DEVELOPER_MODE_ENABLED:
      return 'Developer Mode Changed'
    case DeviceEventType.IS_PLAY_PROTECT_ENABLED:
      return 'Play Protect Changed'
    case DeviceEventType.IS_SIDELOADING_ENABLED:
      return 'Sideloading Changed'
    case DeviceEventType.IS_BIOMETRICS_TRUSTED:
      return 'Biometrics Trusted'
    case DeviceEventType.IS_WORK_PROFILE_CHANGED:
      return 'Work Profile Changed'
    case DeviceEventType.IS_NOTIFICATIONS_ENABLED_CHANGED:
      return 'Notifications Changed'
    case DeviceEventType.OS_VERSION_CHANGED:
      return 'OS Version Changed'
    case DeviceEventType.OS_BUILD_VERSION_CHANGED:
      return 'OS Build Version Changed'
    case DeviceEventType.SMISHING_ENABLED:
      return 'Smishing Changed'
    case DeviceEventType.LOCKDOWN_ENABLED:
      return 'Lockdown Changed'
    case DeviceEventType.FS_METADATA_CREATED:
      return 'FS Diagnostics Created'
    case DeviceEventType.IVERIFY_RECENTLY_INSTALLED:
      return 'iVerify App Recently Installed'
    case DeviceEventType.TELCO:
      return 'Mobile Carrier Changed'
    case DeviceEventType.TELCO_SET:
      return 'Mobile Carrier Set'
    case DeviceEventType.COUNTRY_CODE_SET:
      return 'Country Code Set'
    default:
      return eventType.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
  }
}

export const getCategoryByEventType = (eventType: string) => {
  switch (eventType) {
    case DeviceEventType.ALERT_RAISED:
    case DeviceEventType.ALERT_UPDATED:
    case DeviceEventType.ALERT_CLOSED:
      return getCategoryName(DeviceEventCategory.ALERT)
    case DeviceEventType.PROCESS_CRASH:
    case DeviceEventType.CRASHLOG_CREATED:
    case DeviceEventType.CRASHLOG_DELETED:
      return getCategoryName(DeviceEventCategory.CRASH)
    case DeviceEventType.SCAN_RECEIVED:
      return getCategoryName(DeviceEventCategory.SCANS)
    case DeviceEventType.SHUTDOWN_REBOOT:
    case DeviceEventType.CONTAINERMANAGERD_BOOT:
    case DeviceEventType.BOOT_TIME:
      return getCategoryName(DeviceEventCategory.SYSTEM)
    case DeviceEventType.BIOMETRICS_CHANGED:
    case DeviceEventType.SCREEN_LOCK_CHANGED:
    case DeviceEventType.SMISHING_ENABLED:
    case DeviceEventType.IS_BIOMETRICS_TRUSTED:
      return getCategoryName(DeviceEventCategory.PROTECTION_MEASURES)
    case DeviceEventType.DEVICE_OUT_OF_POLICY:
    case DeviceEventType.DEVICE_IN_POLICY:
      return getCategoryName(DeviceEventCategory.POLICY)
    case DeviceEventType.TELCO:
    case DeviceEventType.COUNTRY_CODE:
    case DeviceEventType.TELCO_SET:
    case DeviceEventType.COUNTRY_CODE_SET:
      return getCategoryName(DeviceEventCategory.NETWORK)
    case DeviceEventType.APP_VERSION_CHANGED:
    case DeviceEventType.IS_NOTIFICATIONS_ENABLED_CHANGED:
    case DeviceEventType.IVERIFY_RECENTLY_INSTALLED:
      return getCategoryName(DeviceEventCategory.APP)
    case DeviceEventType.OS_VERSION_CHANGED:
    case DeviceEventType.OS_BUILD_VERSION_CHANGED:
    case DeviceEventType.IS_PLAY_PROTECT_ENABLED:
    case DeviceEventType.IS_WORK_PROFILE_CHANGED:
    case DeviceEventType.SECURITY_PATCH:
    case DeviceEventType.IS_SIDELOADING_ENABLED:
    case DeviceEventType.IS_DEVELOPER_MODE_ENABLED:
    case DeviceEventType.IS_ADB_ENABLED:
    case DeviceEventType.LOCKDOWN_ENABLED:
    case DeviceEventType.ROOT_DETECTED:
      return getCategoryName(DeviceEventCategory.OS_SECURITY)
    case DeviceEventType.SYSDIAGNOSE_CAPTURED:
    case DeviceEventType.EMPTY_DIRECTORY:
    case DeviceEventType.SYSDIAGNOSE_DELETED:
    case DeviceEventType.SYSDIAGNOSE_CREATED:
    case DeviceEventType.RECORD_DELETED:
    case DeviceEventType.FS_METADATA_CREATED:
      return getCategoryName(DeviceEventCategory.DIAGNOSTICS)
    default:
      return 'Unknown'
  }
}

export const getCategoryName = (eventCategory: DeviceEventCategory) => {
  switch (eventCategory) {
    case DeviceEventCategory.OS_SECURITY:
      return 'OS Security'
    default:
      return eventCategory.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
  }
}

export const getDetails = (eventType: string, event: DeviceEvent) => {
  switch (eventType) {
    case DeviceEventType.OS_VERSION_CHANGED:
    case DeviceEventType.OS_BUILD_VERSION_CHANGED:
    case DeviceEventType.SECURITY_PATCH:
    case DeviceEventType.TELCO:
    case DeviceEventType.BOOT_TIME:
    case DeviceEventType.COUNTRY_CODE:
    case DeviceEventType.ROOT_DETECTED:
    case DeviceEventType.APP_VERSION_CHANGED:
    case DeviceEventType.IS_BIOMETRICS_TRUSTED: {
      const eventDetailsOldToNew = parseEventDetails(event)
      let old = eventDetailsOldToNew?.oldValue ?? 'Unknown'
      if (old === 'null' || old === '') {
        old = 'Unknown'
      }
      if (!eventDetailsOldToNew) {
        return ''
      }
      return 'Updated from ' + old + ' to ' + eventDetailsOldToNew.newValue
    }
    case DeviceEventType.TELCO_SET: {
      const eventDetailsOldToNew = parseEventDetails(event)
      if (!eventDetailsOldToNew) {
        return ''
      }
      return 'Mobile Carrier set to ' + eventDetailsOldToNew.newValue
    }
    case DeviceEventType.COUNTRY_CODE_SET: {
      const eventDetailsOldToNew = parseEventDetails(event)
      if (!eventDetailsOldToNew) {
        return ''
      }
      return (
        'Country Code set to ' + eventDetailsOldToNew.newValue
      )
    }
    case DeviceEventType.BIOMETRICS_CHANGED:
    case DeviceEventType.SCREEN_LOCK_CHANGED:
    case DeviceEventType.IS_NOTIFICATIONS_ENABLED_CHANGED:
    case DeviceEventType.IS_WORK_PROFILE_CHANGED:
    case DeviceEventType.IS_PLAY_PROTECT_ENABLED:
    case DeviceEventType.IS_SIDELOADING_ENABLED:
    case DeviceEventType.IS_DEVELOPER_MODE_ENABLED:
    case DeviceEventType.IS_ADB_ENABLED:
    case DeviceEventType.LOCKDOWN_ENABLED:
    case DeviceEventType.SMISHING_ENABLED: {
      const eventDetailsOldToNew = parseEventDetails(event)
      if (!eventDetailsOldToNew) {
        return ''
      }
      return (
        'Updated from ' +
        booleanToOnOff(eventDetailsOldToNew.oldValue) +
        ' to ' +
        booleanToOnOff(eventDetailsOldToNew.newValue)
      )
    }
    case DeviceEventType.ALERT_RAISED:
      return event.alert?.alertName ? `Alert raised: ${event.alert.alertName}` : ''
    case DeviceEventType.ALERT_UPDATED:
      return event.alert?.alertName ? `Alert updated: ${event.alert.alertName}` : ''
    case DeviceEventType.ALERT_CLOSED:
      return event.alert?.alertName ? `Alert closed: ${event.alert.alertName}` : ''
    case DeviceEventType.PROCESS_CRASH: {
      const eventDetailsCrash = parseEventDetails(event)
      if (!eventDetailsCrash) {
        return ''
      }
      return 'Process ' + eventDetailsCrash.processName + ' with Exception ' + eventDetailsCrash.exceptionType
    }
    case DeviceEventType.EMPTY_DIRECTORY:
    case DeviceEventType.CRASHLOG_CREATED:
    case DeviceEventType.CRASHLOG_DELETED:
    case DeviceEventType.RECORD_DELETED:
    case DeviceEventType.FS_METADATA_CREATED: {
      const eventDetailsCrashLog = parseEventDetails(event)
      return eventDetailsCrashLog?.fileSystemEventDetails || ''
    }
    case DeviceEventType.DEVICE_OUT_OF_POLICY: {
      const eventDetailsPolicy = parseEventDetails(event)
      if (!eventDetailsPolicy) {
        return ''
      }
      if (eventDetailsPolicy.newValue) {
        const newValues: string[] = JSON.parse(eventDetailsPolicy.newValue)
        return ' Out of policy: ' + newValues.join(', ')
      }
      return ''
    }
    case DeviceEventType.IVERIFY_RECENTLY_INSTALLED: {
      const eventDetails = parseEventDetails(event)
      if (!eventDetails) {
        return ''
      }
      return eventDetails.details
    }
    default:
      return ''
  }
}

export const parseEventDetails = (event: DeviceEvent) => {
  if (!event.eventDetails) {
    return null
  }
  const eventDetails = JSON5.parse((event.eventDetails as string) || '{}')
  return eventDetails as EventDetails
}

export const booleanToOnOff = (value: string | undefined) => {
  let valueToReturn = ''
  if (value === null || value === undefined) {
    valueToReturn = ''
  } else {
    valueToReturn = value === 'true' || value === 'True' ? 'on' : 'off'
  }
  return valueToReturn
}

export interface EventDetails {
  oldValue?: string
  newValue?: string
  processName?: string
  exceptionType?: string
  fileSystemEventDetails?: string
  details?: string
}
